import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Button } from '../ui';
import { CloseIcon } from '../icons';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Matches the `duration-200` on the panel and the scrim. */
const TRANSITION_MS = 200;

/**
 * Read live rather than cached: someone can change the setting with the tab
 * open, and the exit needs the answer at the moment it runs. Skipping the delay
 * matters more than skipping the animation -- without it, asking for less motion
 * buys a 200ms dead pause instead.
 */
const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

/**
 * Lets a close button rendered *inside* the panel close the same way Escape and
 * the scrim do. Without it a child would call the caller's navigate directly and
 * teleport out, skipping the slide that every other exit plays.
 */
type SidePanelApi = {
  /** `force` skips the contents' own veto -- for the button that *is* the answer
   *  the veto was asking for ("Discard", "Create"). Without it those buttons
   *  route straight back into the guard that put them on screen. */
  close: (options?: { force?: boolean }) => void;
  setGuard: (guard: (() => boolean) | null) => void;
};

const SidePanelContext = createContext<SidePanelApi>({ close: () => {}, setGuard: () => {} });

export const useSidePanelClose = () => useContext(SidePanelContext).close;

/**
 * Let the panel's contents veto a close.
 *
 * Return `true` to stop it -- an unsaved draft asking for confirmation. This has
 * to live in the shell rather than in the child, because Escape and the scrim
 * are the shell's own handlers: a child listening for Escape itself would still
 * miss the scrim entirely.
 */
export function useSidePanelCloseGuard(guard: () => boolean) {
  const { setGuard } = useContext(SidePanelContext);
  const latest = useRef(guard);
  latest.current = guard;

  useEffect(() => {
    setGuard(() => latest.current());
    return () => setGuard(null);
  }, [setGuard]);
}

/**
 * The panel's top and bottom bars.
 *
 * Both panels wrote the same header class string out longhand, which is exactly
 * how the two drifted apart in padding once. They live here rather than in
 * `ui.tsx` because they are this shell's chrome, not a shared primitive -- the
 * only thing that renders them is a child of SidePanel.
 */
export function PanelHeader({
  onClose,
  closeLabel,
  children,
}: {
  onClose: () => void;
  /** "Discard this task" and "Close task" are not the same promise. */
  closeLabel: string;
  children: ReactNode;
}) {
  return (
    // A tint rather than plain white: the body below is a form on white, and an
    // unpainted header only separates from it by a hairline.
    <header className="flex shrink-0 items-start gap-2 border-b border-stone-200 bg-stone-50 px-4 py-2.5">
      {children}
      <Button compact variant="ghost" aria-label={closeLabel} onClick={onClose}>
        <CloseIcon />
      </Button>
    </header>
  );
}

/**
 * Pinned actions.
 *
 * A sibling of the scrolling body rather than something positioned: the panel is
 * already `flex flex-col`, so `flex-1` on the body and `shrink-0` here is the
 * whole mechanism. It sits inside the panel element, so the Tab trap above keeps
 * covering it without knowing it exists.
 *
 * Only for a bar with a primary action. A panel whose bottom row is a delete
 * wants that scrolled away, not held permanently in view.
 */
export function PanelFooter({ children }: { children: ReactNode }) {
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-stone-200 bg-white px-4 py-2.5">
      {children}
    </div>
  );
}

/**
 * The right-hand overlay shell: scrim, panel, `role="dialog"`, focus trap,
 * Escape, and the slide in and out.
 *
 * Built the way Layout builds its mobile drawer -- conditional render, fixed
 * positioning, no portal and no dependency. It knows nothing about tasks, so the
 * same shell holds a saved task and an unsaved draft and stays mounted across
 * the switch between them.
 */
export function SidePanel({
  labelledBy,
  focusKey,
  onClose,
  children,
}: {
  labelledBy: string;
  /** Changes when the *contents* are swapped. The shell deliberately survives
   *  that swap, so nothing else would move focus back into the panel. */
  focusKey: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const guardRef = useRef<(() => boolean) | null>(null);

  // A ref rather than state: registering a guard must not re-render the shell,
  // and `close` must never capture a stale one.
  const setGuard = useCallback((guard: (() => boolean) | null) => {
    guardRef.current = guard;
  }, []);

  // Reduced motion starts open: there is nothing to animate from, and flipping a
  // frame later would only flash the panel off-screen once.
  const [open, setOpen] = useState(prefersReducedMotion);
  const [closing, setClosing] = useState(false);

  /**
   * Enter.
   *
   * A forced style recalculation while the closed classes still apply, so the
   * off-screen transform becomes the transition's before-change style. A
   * `requestAnimationFrame` cannot promise that: rAF callbacks run *before* the
   * frame is painted, and React flushes effects synchronously inside the click
   * that opened the panel -- so it would only work by way of how the scheduler
   * happens to defer the state update. A layout effect commits before paint, so
   * the animation starts on the first painted frame either way.
   */
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel || open) return;
    panel.getBoundingClientRect();
    setOpen(true);
    // Mount only. Re-running would re-open a panel that is on its way out.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Exit: play the slide out first, then navigate.
   *
   * The panel lives in a route, so navigating immediately would unmount it
   * mid-frame with nothing left to animate. Holding the route matched for the
   * duration is what buys the exit.
   *
   * A timeout rather than a `transitionend` listener, which is unusable here for
   * two reasons: it bubbles, and `transition` is on buttons and rows throughout
   * the panel, so hovering anything inside would end the close early; and it
   * does not fire at all when the element is not painted.
   *
   * The timeout is not trusted either. A background tab throttles timers to a
   * second or more, so the panel can sit in its closed state far longer than
   * 200ms -- which is why the closed state is `pointer-events-none` and `inert`.
   * A late timer then removes something already invisible and untouchable
   * instead of leaving an unclickable sheet over the whole page.
   */
  const close = useCallback(
    (options?: { force?: boolean }) => {
      if (closing) return;
      // The contents get a veto -- e.g. an unsaved draft raising its own inline
      // confirmation instead of vanishing. `force` is how the draft then answers
      // its own question: Discard and Create must not be handed back to the
      // guard that raised the prompt.
      if (!options?.force && guardRef.current?.()) return;

      if (prefersReducedMotion()) {
        onClose();
        return;
      }

      setClosing(true);
      setOpen(false);
    },
    [closing, onClose],
  );

  useEffect(() => {
    if (!closing) return;
    const timer = window.setTimeout(onClose, TRANSITION_MS);
    // Cleared on unmount, so a route that goes away during the exit -- the Back
    // button, an auth redirect -- cannot fire a navigation afterwards and drag
    // the user back out of wherever they just went.
    return () => window.clearTimeout(timer);
  }, [closing, onClose]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      // An inline editor or a nested control handles Escape first and marks it;
      // otherwise one keypress would cancel an edit *and* close the panel.
      if (event.key === 'Escape' && !event.defaultPrevented) close();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [close]);

  /**
   * Focus the panel on open, and again whenever its contents are replaced.
   *
   * The second half is the one that is easy to miss: following a subtask or
   * prerequisite link swaps the whole body under the user, focus falls to
   * `<body>`, and the Tab trap below stops matching -- so Tab would walk the
   * page behind a dialog that claims to be modal.
   */
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    // Only when focus has actually escaped. Focusing unconditionally would pull
    // the caret out of whatever the contents deliberately auto-focused -- the
    // new-task form's title field is the obvious casualty.
    if (panel.contains(document.activeElement)) return;
    panel.focus();
  }, [focusKey]);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    restoreRef.current = document.activeElement as HTMLElement | null;

    function trapTab(event: KeyboardEvent) {
      if (event.key !== 'Tab') return;
      // Queried per keypress rather than cached: sections load and editors open,
      // so a cached list goes stale within seconds.
      const focusable = panel!.querySelectorAll<HTMLElement>(FOCUSABLE);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    panel.addEventListener('keydown', trapTab);
    return () => {
      panel.removeEventListener('keydown', trapTab);
      // Only if the caret is still ours to give back: by the time the exit
      // finishes the user may have deliberately clicked something else, and
      // yanking focus to the row they came from would be worse than losing it.
      const active = document.activeElement;
      const ours = panel.contains(active) || active === document.body || active === null;
      if (ours && restoreRef.current?.isConnected) restoreRef.current.focus();
    };
  }, []);

  return (
    <SidePanelContext.Provider value={{ close, setGuard }}>
      <div
        // Wrapped: `close` now takes options, and the click event is not one.
        onClick={() => close()}
        aria-hidden="true"
        className={`fixed inset-0 z-40 bg-stone-900/40 transition-opacity duration-200 ease-out motion-reduce:transition-none ${
          open ? 'opacity-100' : 'opacity-0'
        } ${closing ? 'pointer-events-none' : ''}`}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        // Keyed on `closing`, not on `!open`. Both are false for the single
        // frame the panel spends off-screen on the way in, and `inert` there
        // would swallow the autoFocus the contents ask for -- an inert subtree
        // cannot take focus at all, so the new-task title field came up blank.
        inert={closing}
        className={`fixed inset-y-0 right-0 z-50 flex w-full max-w-xl flex-col border-l border-stone-200 bg-white shadow-xl transition-transform duration-200 ease-out focus:outline-none motion-reduce:transition-none lg:max-w-2xl ${
          open ? 'translate-x-0' : 'translate-x-full'
        } ${closing ? 'pointer-events-none' : ''}`}
      >
        {children}
      </div>
    </SidePanelContext.Provider>
  );
}
